import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { ReactComponent as TrashIcon } from 'assets/ui/trash.svg';

import { Collaborator, UserRole, UserStatus } from 'services/share.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import {
  getCollaborators,
  removeCollaborator,
  shareProject,
  shareProjectToSupport,
} from 'components/app/_mediator/app-share.slice';
import { setModal } from 'components/app/_mediator/app.slice';
import { fetchProjects } from 'components/app/_mediator/app-data.slice';

import { Modal } from 'components/UI/modal/modal';
import { Button } from 'components/UI/button/button';
import { InputGroup } from 'components/UI/input/input-group';
import { InputBase } from 'components/UI/input/elements/input-base';
import { Tooltip } from 'components/UI/tooltip/tooltip.tsx';
import { Select } from 'components/UI/select/select';
import { Loader } from 'components/UI/loader/loader';

import { HeaderAction } from './header-action.tsx';

import styles from './_elements.module.scss';

type Props = {
  isOpen: boolean;
};

export const ShareProjectModal = ({ isOpen }: Props) => {
  const selectedProject = useAppSelector((state) => state.projects.selectedProject);
  const user = useAppSelector((state) => state.app__data.user);
  const dispatch = useAppDispatch();

  const [collaboratorIDInfo, setCollaboratorIDInfo] = useState<ProjectId | null>(null);
  const [fetching, setFetching] = useState<boolean>(false);
  const [userNameEmail, setUserNameEmail] = useState('');
  const [userRole, setUserRole] = useState(UserRole.Reader);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const userRoleInProject = collaborators.find((e) => e.userId == user?.id)?.role;

  useEffect(() => {
    isOpen && getProjectCollaborators();
  }, [isOpen]);

  const closeModal = () => {
    dispatch(setModal(0));
  };

  const getProjectCollaborators = async () => {
    if (!selectedProject) return;
    setFetching(true);
    const data = await dispatch(getCollaborators(selectedProject));
    setCollaborators(data.payload as Collaborator[]);
    setFetching(false);
  };

  const submitSupport = async () => {
    closeModal();
    if (!selectedProject) return;
    await dispatch(shareProjectToSupport(selectedProject)).unwrap();
    toast.success('Invitation sent!');
  };

  const submit = async () => {
    closeModal();
    setUserNameEmail('');
    await dispatch(shareProject({ query: userNameEmail, role: userRole })).unwrap();
    toast.success('Invitation sent!');
  };

  const handleDelete = async (userId: number) => {
    if (!selectedProject) return;
    await dispatch(removeCollaborator({ projectId: selectedProject, userId }));
    if (userId !== user?.id) dispatch(getProjectCollaborators);
    else {
      closeModal();
      dispatch(fetchProjects());
    }
  };

  return (
    <Modal className={styles.ShareModal} onRequestClose={closeModal} isOpen={isOpen}>
      <h3>Share your project with another Pulse user</h3>
      <Button outlined onClick={submitSupport}>
        Share with Support
      </Button>
      <div className={styles.ShareModal__inputs}>
        <InputGroup header="Name or Email">
          <InputBase
            data-testid="modal-input"
            onChange={(e) => setUserNameEmail(e.target.value)}
            value={userNameEmail}
            autoFocus
          />
        </InputGroup>
        <InputGroup header="Role">
          <Select
            disabledOptions
            options={[
              { label: 'Editor', value: UserRole.Owner, disabled: true },
              { label: 'Reader', value: UserRole.Reader },
            ]}
            value={userRole}
            onChange={(e) => setUserRole(parseInt(e.target.value))}
          />
        </InputGroup>
      </div>
      <footer>
        <Button outlined onClick={closeModal}>
          Cancel
        </Button>
        <Button onClick={submit}>Share</Button>
      </footer>

      <div className={styles.ShareModal__list}>
        <h3>Collaborators:</h3>
        <ul>
          {fetching || !collaborators ? (
            <Loader className={styles.ShareModal__loader} />
          ) : (
            collaborators.map((collaborator: Collaborator) => (
              <li
                key={collaborator.userId}
                onClick={() =>
                  setCollaboratorIDInfo(collaborator.userId === collaboratorIDInfo ? null : collaborator.userId)
                }
              >
                <div>
                  <p className={styles.ShareModal__collaboratorName}>
                    {collaborator.userName}
                    <h4>{collaborator.userId === user?.id && '(you)'}</h4>
                  </p>
                  <Tooltip
                    tip={<CollaboratorInfo collaborator={collaborator} />}
                    showTooltip={collaborator.userId !== collaboratorIDInfo}
                    position="right"
                  >
                    <HeaderAction
                      type="delete"
                      icon={<TrashIcon />}
                      onSubmit={(e) => {
                        e.stopPropagation();
                        handleDelete(collaborator.userId);
                      }}
                      disabled={
                        collaborator.role === UserRole.Owner ||
                        (userRoleInProject !== UserRole.Owner && collaborator.userId !== user?.id)
                      }
                    />
                  </Tooltip>
                </div>
                {collaborator.userId === collaboratorIDInfo && <CollaboratorInfo collaborator={collaborator} />}
              </li>
            ))
          )}
        </ul>
      </div>
    </Modal>
  );
};

const CollaboratorInfo = ({ collaborator }: { collaborator: Collaborator }) => (
  <div className={styles.ShareModal__collaboratorInfo} onClick={(e) => e.stopPropagation()}>
    <p>
      <span>Email:</span> {collaborator.email}
    </p>
    <p>
      <span>Role:</span> {UserRole[collaborator.role]}
    </p>
    <p>
      <span>Status:</span> {UserStatus[collaborator.status]}
    </p>
    <p>
      <span>Full name:</span> {collaborator.fullName}
    </p>
  </div>
);
